import { useCallback, useRef } from 'react';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useTeamStore } from '@/hooks/useTeamStore';
import { TeamStateReducer } from '@/store/team';

type ListPosition = TeamStateReducer['selectedPosition'];
type ListFilters = Pick<TeamStateReducer, 'selectedPosition'>;

// https://agea.atlassian.net/browse/DTA-10101 - https://agea.atlassian.net/browse/DTA-10107
// conserva lo ultimo que el usuario estaba viendo en el listado lateral (desktop, ventana previa a la veda)
// luego de acciones en cancha (transferir, cambio, capitan):
// - el filtro/orden no se resetea, queda el ultimo aplicado
// - la posicion la pisa el jugador seleccionado en cancha: se restaura la del usuario, salvo que la haya cambiado durante la accion
export const usePersistListFilters = () => {
  const { state } = useTeamStore();
  const { isDesktop } = useIsDesktop();
  const isEnabled = Boolean(state.isBeforeVeda && isDesktop);

  const selectedPositionRef = useRef<ListPosition>(state.selectedPosition);
  selectedPositionRef.current = state.selectedPosition;

  // posicion del usuario previa a la accion en cancha
  const userPositionRef = useRef<ListPosition>(null);
  // posicion seteada por el jugador seleccionado en cancha (undefined: sin accion en curso)
  const fieldPositionRef = useRef<ListPosition | undefined>(undefined);

  // al seleccionar un jugador en cancha guardo la posicion del usuario (solo la primera vez)
  const freezeListPosition = useCallback((fieldPosition: ListPosition) => {
    if (fieldPositionRef.current === undefined) {
      userPositionRef.current = selectedPositionRef.current;
    }
    fieldPositionRef.current = fieldPosition ?? null;
  }, []);

  // devuelve que restaurar (null si no aplica: reset completo)
  const getListFiltersToRestore = useCallback((): ListFilters | null => {
    const fieldPosition = fieldPositionRef.current;
    fieldPositionRef.current = undefined;

    if (!isEnabled) return null;

    const userChangedPosition =
      fieldPosition === undefined ||
      selectedPositionRef.current !== fieldPosition;

    return userChangedPosition
      ? {}
      : { selectedPosition: userPositionRef.current };
  }, [isEnabled]);

  return { freezeListPosition, getListFiltersToRestore };
};
